import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Printer, FileDown, ListChecks, Star, Loader2, Settings2, AlertTriangle, Save, Info, ChevronDown } from "lucide-react";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { QtyInput } from "./QtyInput";
import { fmtKind, unitLabel, unitLengthLabel, inputValue, inputToIP } from "../../lib/units";
import RoomReportPage, { modelDisplayName } from "./RoomReportPage";
import UnitSplitModal from "./UnitSplitModal";
import { generateProjectSummaryPDF, generateSubmittalPDF, generateDetailedRoomReportsPDF } from "../../lib/pdfReport";
import { buildUnitSchedule, lengthCapacityList, isPrimaryAir } from "../../lib/unitSchedule";
import { exportScheduleCSV, exportScheduleXLSX, exportSummaryScheduleCSV, exportSummaryScheduleXLSX } from "../../lib/scheduleExport";
import { isModelN, isACB, isCBFamily, getProjectFamilies, getRoomStaticPressureInwg } from "../../lib/wizardCalc";
import { uploadProjectReport } from "../../lib/reportsApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/collapsible";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

// Inline editable water-flow input. Keeps a local draft while the user
// types; commits the IP value (GPM) on blur or Enter.
function WaterFlowInput({ value, isSI, disabled, onCommit, testid, ...rest }) {
  const toDisplay = (gpm) => {
    if (gpm == null || isNaN(gpm)) return "";
    return isSI ? (gpm * 0.063090196).toFixed(2) : parseFloat(gpm).toFixed(2);
  };
  const [draft, setDraft] = React.useState(() => toDisplay(value));
  React.useEffect(() => { setDraft(toDisplay(value)); }, [value, isSI]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = () => {
    const display = parseFloat(draft);
    if (isNaN(display)) { setDraft(toDisplay(value)); return; }
    const gpm = isSI ? display / 0.063090196 : display;
    if (Math.abs(gpm - value) > 0.001) onCommit(gpm);
  };
  return (
    <input
      type="number"
      step="0.01"
      min="0"
      disabled={disabled}
      value={draft}
      onChange={(ev) => setDraft(ev.target.value)}
      onBlur={commit}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") ev.currentTarget.blur();
        if (ev.key === "Escape") { setDraft(toDisplay(value)); ev.currentTarget.blur(); }
      }}
      onClick={(ev) => ev.stopPropagation()}
      className="h-7 w-20 rounded border border-input bg-background px-1.5 text-right text-xs font-mono tabular-nums focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testid}
      title={`Water flow${isSI ? " (L/s)" : " (GPM)"} — edit to override, Enter/blur to apply`}
      {...rest}
    />
  );
}

// Inline editable PA flow input (CFM in IP, L/s in SI). Commits CFM on
// blur or Enter. ACB rows only — FACB models have no engineer-tunable PA
// stream, and Model N owns its own RA cascade.
function PAFlowInput({ value, isSI, disabled, onCommit, testid, ...rest }) {
  const toDisplay = (cfm) => {
    if (cfm == null || isNaN(cfm)) return "";
    return isSI ? (cfm * 0.4719474).toFixed(1) : parseFloat(cfm).toFixed(0);
  };
  const [draft, setDraft] = React.useState(() => toDisplay(value));
  React.useEffect(() => { setDraft(toDisplay(value)); }, [value, isSI]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = () => {
    const display = parseFloat(draft);
    if (isNaN(display)) { setDraft(toDisplay(value)); return; }
    const cfm = isSI ? display / 0.4719474 : display;
    if (Math.abs(cfm - (parseFloat(value) || 0)) > 0.5) onCommit(cfm);
  };
  return (
    <input
      type="number"
      step={isSI ? "0.5" : "1"}
      min="0"
      disabled={disabled}
      value={draft}
      onChange={(ev) => setDraft(ev.target.value)}
      onBlur={commit}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") ev.currentTarget.blur();
        if (ev.key === "Escape") { setDraft(toDisplay(value)); ev.currentTarget.blur(); }
      }}
      onClick={(ev) => ev.stopPropagation()}
      className="h-7 w-20 rounded border border-input bg-background px-1.5 text-right text-xs font-mono tabular-nums focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testid}
      title={`PA flow${isSI ? " (L/s)" : " (CFM)"} — edit to override, Enter/blur to apply`}
      {...rest}
    />
  );
}

// Inline spigot / air-inlet bore dropdown. ACB-family rows only. Commits
// the chosen bore (4 / 6 / 8 / 10 inches) on change — drives NC for the
// spigot-dependent models (Model C, C1, D); flow-only ACBs (H, I-*, L-*)
// ignore the value but the field still ships to the backend / ordering code.
function SpigotInput({ value, disabled, onCommit, testid, ...rest }) {
  const v = parseInt(value, 10);
  const safe = [4, 6, 8, 10].includes(v) ? v : 6;
  return (
    <select
      value={safe}
      disabled={disabled}
      onChange={(ev) => {
        const next = parseInt(ev.target.value, 10);
        if (Number.isFinite(next) && next !== safe) onCommit(next);
      }}
      onClick={(ev) => ev.stopPropagation()}
      className="h-7 w-16 rounded border border-input bg-background px-1 text-xs font-mono tabular-nums focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testid}
      title='Spigot (air-inlet) bore. NC depends on this for Model C/C1/D; flow-only ACBs (H, I-*, L-*) ignore it.'
      {...rest}
    >
      <option value={4}>4&quot;</option>
      <option value={6}>6&quot;</option>
      <option value={8}>8&quot;</option>
      <option value={10}>10&quot;</option>
    </select>
  );
}

// Inline editable static pressure input. Commits the IP value (in.wg) on
// blur or Enter, mirroring the behaviour of the existing SP input in ACB rows.
function SPInput({ spInwg, placeholder, isSI, disabled, onCommit, testid, ...rest }) {
  const [draft, setDraft] = React.useState(inputValue(spInwg, "staticPressure", isSI, isSI ? 0 : 3));
  React.useEffect(() => {
    setDraft(inputValue(spInwg, "staticPressure", isSI, isSI ? 0 : 3));
  }, [spInwg, isSI]);
  const commit = () => {
    const ipVal = inputToIP(draft, "staticPressure", isSI);
    const num = parseFloat(ipVal);
    onCommit(isNaN(num) ? undefined : num);
  };
  return (
    <input
      type="number"
      step={isSI ? "1" : "0.001"}
      min="0"
      disabled={disabled}
      placeholder={placeholder}
      value={draft}
      onChange={(ev) => setDraft(ev.target.value)}
      onBlur={commit}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") ev.currentTarget.blur();
        if (ev.key === "Escape") ev.currentTarget.blur();
      }}
      onClick={(ev) => ev.stopPropagation()}
      className="h-7 w-20 rounded border border-input bg-background px-1.5 text-right text-xs font-mono tabular-nums focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={testid}
      {...rest}
    />
  );
}

export default function SummaryTab({ project, rooms, results, configurations, isSI: isSIProp, onUpdatePlan, onUpdateLength, onUpdateQty, onSetDefaultQtyMode, onUpdateRoomStaticPressure, onUpdateRoomWaterFlow, onUpdateRoomPAFlow, onUpdateRoomSpigot, onUpdateRoomNotes, onSave, onPdfGenerated, selectionMode = "initial", running = false, resultsStale = false, onRerunSelection, onOptimize, onAccept, onIndividual, selectionReport = null, rowLoading = null }) {
  // isSI is now driven by the global header toggle (lifted to ProjectWizard).
  // We keep an internal fallback for legacy embed usage.
  const [isSILocal, setIsSILocal] = useState(false);
  const isSI = typeof isSIProp === "boolean" ? isSIProp : isSILocal;
  const isControlled = typeof isSIProp === "boolean";
  const setIsSI = (v) => {
    if (!isControlled) setIsSILocal(v);
  };
  const [openIdx, setOpenIdx] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [submittalBusy, setSubmittalBusy] = useState(false);
  const [savingBusy, setSavingBusy] = useState(false);
  const [splitIdx, setSplitIdx] = useState(null);

  // Detailed Report picker modal
  const [detailPickerOpen, setDetailPickerOpen] = useState(false);
  const [detailSelectedIdxs, setDetailSelectedIdxs] = useState([]);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailProgress, setDetailProgress] = useState(null); // { done, total, label } | null
  // QTY-mode confirmation: when the user bumps QTY from 1 → ≥2 we ask whether
  // the room load should be DIVIDED across the units (default — engineer
  // sizes each unit for room load / qty + OA / qty) or DUPLICATED (each unit
  // independently handles the FULL room load + FULL OA — e.g. parallel zones,
  // redundancy). Stored as { idx, qty } until the user picks an option.
  //
  // When the dialog opens, the engineer can tick "Apply this choice to all
  // rooms in this project". Picking an option with the checkbox ticked
  // stores the choice on `project.default_qty_mode`, which causes future
  // QTY > 1 commits to use that mode silently for every room in the project.
  const [qtyModeAsk, setQtyModeAsk] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);

  // B.1.6 — Fill SP dialog: bulk-fill pa_static_pressure_inwg for all ACB rooms
  const [fillSPOpen, setFillSPOpen] = useState(false);
  const [fillSPValue, setFillSPValue] = useState("");

  // ACB room detection: determines whether PA Flow column and Fill SP button
  // are visible (ACB = induction models only: C, C1, H, I-*, L-*).
  const hasACBRooms = useMemo(
    () => (rooms || []).some((r) => isACB(r.unit_model)),
    [rooms]
  );
  // CB-family detection: controls whether the Static Pressure and Water Flow
  // editable columns appear. Covers ACB + FACB (S, S-HW, O-1, O-2) but
  // excludes Model N / N-f (EWS family — read-only rows).
  const hasCBFamilyRooms = useMemo(
    () => (rooms || []).some((r) => isCBFamily(r.unit_model)),
    [rooms]
  );
  // Auto-tick when the engineer has many rooms — they almost certainly want
  // the same choice everywhere, and they can untick it if not. Also auto-tick
  // when a project-wide default already exists (the engineer once decided to
  // apply across the board and is unlikely to deviate per-room now).
  useEffect(() => {
    if (qtyModeAsk) {
      setApplyToAll((rooms?.length || 0) > 1 || !!project?.default_qty_mode);
    }
  }, [qtyModeAsk, rooms, project]);

  const handleQtyCommit = useCallback((idx, v) => {
    const next = Math.max(0, parseInt(v, 10) || 0);
    const currentQty = results?.[idx]?.summary?.plan?.qty || 1;
    const currentMode = results?.[idx]?.summary?.plan?.qty_mode;
    const projectDefaultMode = project?.default_qty_mode;
    // Always ask on qty → N>1 transitions, even when a project default exists
    // or a per-row mode is already set. Past confusion: a stale project default
    // silently routed every qty change through divide mode, producing physically
    // nonsensical per-unit selections (e.g. 29 units each handling ~147 W when
    // the smallest unit delivers ~1,783 W).
    if (next > 1 && next !== currentQty) {
      setQtyModeAsk({ idx, qty: next });
      return;
    }
    const mode = currentMode || projectDefaultMode || "divide";
    if (onUpdateQty) onUpdateQty(idx, next, mode);
  }, [results, onUpdateQty, project]);

  // Apply the picked mode to either just the asked-for row, or to every
  // existing multi-unit row in the project when "Apply to all" is ticked.
  const handlePickQtyMode = useCallback((mode) => {
    const ask = qtyModeAsk;
    if (!ask) return;
    setQtyModeAsk(null);
    if (applyToAll && onSetDefaultQtyMode) {
      onSetDefaultQtyMode(mode);
      // Also re-stamp every existing multi-unit row so they all align.
      (results || []).forEach((r, i) => {
        const q = r?.summary?.plan?.qty || 1;
        if (q > 1 && onUpdateQty) onUpdateQty(i, q, mode);
      });
    }
    if (onUpdateQty) onUpdateQty(ask.idx, ask.qty, mode);
  }, [qtyModeAsk, applyToAll, onSetDefaultQtyMode, results, onUpdateQty]);
  // Schedule export options dialog
  const [scheduleOpen, setScheduleOpen] = useState(false); // legacy export-options dialog state — retained for potential future use
  const [optExcel, setOptExcel] = useState(true);
  const [optPdf, setOptPdf] = useState(true);
  const [optCsv, setOptCsv] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  const handlePrint = () => window.print();

  const handleSaveSchedule = async () => {
    // "Save Schedule" — downloads a compact per-variant schedule file
    // (Variant | Model | Length | QTY) as Excel + CSV. Different from the
    // background DB auto-save which captures the full project state.
    setSavingBusy(true);
    try {
      await exportSummaryScheduleXLSX({ project, rooms, results, configurations, isSI });
      exportSummaryScheduleCSV({ project, rooms, results, configurations, isSI });
      toast.success("Schedule exported (XLSX + CSV)");
    } catch (e) {
      toast.error("Failed to export schedule: " + (e.message || "unknown"));
    } finally {
      setSavingBusy(false);
    }
  };

  const handleExportUnitSchedule = async () => {
    // "Unit Schedule" — exports the full per-room performance schedule
    // (OA, EA, capacities, water, PD) directly as BOTH .xlsx + .csv.
    // No format-picker dialog: the user wants one click → two files.
    setExportBusy(true);
    try {
      await exportScheduleXLSX({ project, rooms, results, isSI });
      exportScheduleCSV({ project, rooms, results, isSI });
      toast.success("Unit Schedule exported (XLSX + CSV)");
      if (onPdfGenerated) onPdfGenerated();
    } catch (e) {
      toast.error("Export failed: " + (e.message || "unknown"));
    } finally {
      setExportBusy(false);
    }
  };

  const handleExportSchedule = async () => {
    if (!optExcel && !optPdf && !optCsv) {
      toast.error("Pick at least one format");
      return;
    }
    setExportBusy(true);
    try {
      if (optExcel) {
        await exportScheduleXLSX({ project, rooms, results, isSI });
      }
      if (optCsv) {
        exportScheduleCSV({ project, rooms, results, isSI });
      }
      if (optPdf) {
        await generateProjectSummaryPDF({ project, rooms, results, configurations, isSI });
      }
      toast.success("Schedule exported");
      if (onPdfGenerated) onPdfGenerated();
      setScheduleOpen(false);
    } catch (e) {
      toast.error("Export failed: " + (e.message || "unknown"));
    } finally {
      setExportBusy(false);
    }
  };
  const handleOpenDetailPicker = () => {
    // Pre-select all rooms that have a valid result.
    const preSelected = (rooms || [])
      .map((_, idx) => idx)
      .filter((idx) => results?.[idx]?.ok);
    setDetailSelectedIdxs(preSelected);
    setDetailProgress(null);
    setDetailPickerOpen(true);
  };

  const handleDetailToggleRoom = (idx) => {
    setDetailSelectedIdxs((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleDetailSelectAll = () => {
    setDetailSelectedIdxs(
      (rooms || []).map((_, idx) => idx).filter((idx) => results?.[idx]?.ok)
    );
  };

  const handleDetailSelectNone = () => setDetailSelectedIdxs([]);

  const handleDetailSelectIssues = () => {
    // Select rooms where delivered capacity is < 95% or > 110% of target.
    const issueIdxs = (rooms || [])
      .map((r, idx) => {
        if (!results?.[idx]?.ok) return null;
        const s = results[idx]?.summary || {};
        const plan = s.plan || {};
        const tgt = parseFloat(r.cooling_total_btuh) || parseFloat(r.cooling_sensible_btuh) || 0;
        if (!(tgt > 0)) return null;
        const planQty = plan?.qty || 1;
        const planUnits = plan?.units || [];
        const distinctLengths = new Set(planUnits.map((u) => u.length_ft).filter((l) => l != null));
        const isSplit = planUnits.length > 1 && distinctLengths.size > 1;
        const isDuplicate = plan?.qty_mode === "duplicate";
        const mult = (isSplit || isDuplicate) ? 1 : planQty;
        const delivered = (parseFloat(s.cooling_total_btuh) || parseFloat(s.coil_sensible_btuh) || 0) * mult;
        const pct = tgt > 0 ? (delivered / tgt) * 100 : null;
        if (pct === null) return null;
        return (pct < 95 || pct > 110) ? idx : null;
      })
      .filter((v) => v !== null);
    setDetailSelectedIdxs(issueIdxs);
  };

  const handleGenerateDetailedReport = async () => {
    if (detailSelectedIdxs.length === 0) {
      toast.error("Select at least one room.");
      return;
    }
    setDetailBusy(true);
    setDetailProgress({ done: 0, total: detailSelectedIdxs.length, label: "Starting…" });
    try {
      await generateDetailedRoomReportsPDF({
        project,
        rooms,
        results,
        selectedRoomIdxs: detailSelectedIdxs,
        isSI,
        onProgress: (p) => setDetailProgress(p),
      });
      toast.success(`Detailed Report generated (${detailSelectedIdxs.length} room${detailSelectedIdxs.length !== 1 ? "s" : ""})`);
      setDetailPickerOpen(false);
    } catch (e) {
      toast.error("Detailed Report failed: " + (e.message || "unknown"));
    } finally {
      setDetailBusy(false);
      setDetailProgress(null);
    }
  };

  const handleSubmittal = async () => {
    if (!rooms || !rooms.length) {
      toast.error("No rooms to include in the Selection Submittal.");
      return;
    }
    // Detail-data guard.
    // Per Phase 3c, results.raw is stripped from the project payload when it
    // is saved to the DB. When the user opens an existing project, the
    // in-memory `results` array only carries `summary` (enough for the
    // Schedule and Selection Summary table) but not `raw.results` (which the
    // per-room PDF mount needs for the full detail block). Detect that case
    // and ask the user to re-run before generating, so the PDF doesn't ship
    // pages full of "No detailed result available."
    const needsRerun = (results || []).some((r, i) => {
      if (!r?.ok) return false;
      const room = rooms[i];
      if (!room) return false;
      const isNModel = room.unit_model === "Model N" || room.unit_model === "Model N-f";
      if (isNModel) {
        return !r.raw?.summary_table;
      }
      return !(r.raw?.results?.length);
    });
    if (needsRerun) {
      toast.error(
        "Selection detail is missing — Re-run Selections before generating the Selection Report. " +
        "Use the Re-run button above this card.",
        { duration: 6000 }
      );
      return;
    }
    setSubmittalBusy(true);
    try {
      // Selection Report is the LIGHTWEIGHT Schedule Summary path — just
      // project info, design conditions, the chunked room schedule, and the
      // unit schedule. Skips the per-room detail captures and the Configuration
      // Schedule (BOM) because both scale O(rooms) and crash html2canvas on
      // 232-room projects. The Submittal Package tab still uses
      // generateSubmittalPDF for the full heavy report when engineers
      // explicitly request it; per-room detail can be generated in batches
      // via the "Detailed Report" button alongside this one.
      const blob = await generateProjectSummaryPDF({
        project,
        rooms,
        results,
        configurations,
        isSI,
        summaryOnly: true,
        returnBlob: true,
      });
      // Trigger the local download.
      const safeName = (project?.name || "Project").replace(/[^a-zA-Z0-9-]/g, "_");
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `ZAMBA_${safeName}_SelectionReport_${stamp}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
      // Persist to the backend so the Submittal tab can compile without
      // re-running the per-room canvas-capture pipeline. Non-fatal.
      try {
        if (project?.id) {
          await uploadProjectReport(project.id, "selection", blob, filename);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Selection Report uploaded copy failed:", e);
      }
      toast.success("Selection Report generated & saved");
      if (onPdfGenerated) onPdfGenerated("selection");
    } catch (e) {
      toast.error("Selection Submittal failed: " + (e.message || "unknown"));
    } finally {
      setSubmittalBusy(false);
    }
  };

  const enriched = rooms.map((r, idx) => ({
    room: r,
    result: results[idx],
    summary: results[idx]?.summary || {},
    idx,
  }));

  // Find extremes
  const extremes = useMemo(() => {
    const valid = enriched.filter((e) => e.result?.ok);
    if (!valid.length) return null;
    const byMetric = (key) =>
      valid.reduce((best, cur) => {
        const v = parseFloat(cur.summary[key]) || 0;
        const bv = parseFloat(best.summary[key]) || 0;
        return v > bv ? cur : best;
      }, valid[0]);
    return {
      capacity: byMetric("cooling_total_btuh"),
      oa: byMetric("oa_cfm"),
      heating: byMetric("heating_btuh"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  // Full-page per-room report mode (placed AFTER all hook calls)
  if (openIdx !== null) {
    return (
      <RoomReportPage
        project={project}
        room={rooms[openIdx]}
        result={results[openIdx]}
        isSI={isSI}
        onBack={() => setOpenIdx(null)}
        onPdfGenerated={onPdfGenerated}
        onUpdateLength={onUpdateLength ? (newLen, newCkts) => onUpdateLength(openIdx, newLen, newCkts) : undefined}
      />
    );
  }

  const totals = enriched.reduce(
    (a, e) => {
      const s = e.summary;
      const plan = s.plan || {};
      const units = plan.units && plan.units.length
        ? plan.units
        : [{ qty: plan.qty ?? 1, per_unit_total: parseFloat(s.cooling_total_btuh) || 0, result: null }];
      const isSingleVariant = units.length === 1;
      const singleVariantQty = plan.qty ?? units[0]?.qty ?? 1;
      units.forEach((u) => {
        const subQty = isSingleVariant ? singleVariantQty : (u.qty || 1);
        const subSum = u.result?.summary || s;
        const perOa = u.per_unit_oa != null
          ? parseFloat(u.per_unit_oa)
          : (parseFloat(subSum.oa_cfm) || parseFloat(e.room.oa_flow_cfm) || 0);
        a.qty += subQty;
        a.oa += perOa * subQty;
        a.ea += (parseFloat(subSum.ea_cfm) || parseFloat(e.room.ea_flow_cfm) || 0) * subQty;
        // Prefer the modulated PA+Coil sum (cooling_total_btuh) over the
        // un-modulated `per_unit_total` (max coil capacity at full GPM).
        // For PA models these diverge: the modulated total ≈ room target.
        a.cool_total += (parseFloat(subSum.cooling_total_btuh) || parseFloat(u.per_unit_total) || 0) * subQty;
        a.pa_sens += (parseFloat(subSum.pa_sensible_btuh) || 0) * subQty;
        a.pa_lat += (parseFloat(subSum.pa_latent_btuh) || 0) * subQty;
        a.coil_sens += (parseFloat(subSum.coil_sensible_btuh) || 0) * subQty;
        a.coil_lat += (parseFloat(subSum.coil_latent_btuh) || 0) * subQty;
        a.heat += (parseFloat(subSum.heating_btuh) || 0) * subQty;
        a.heat_pa += (parseFloat(subSum.heating_pa_btuh) || 0) * subQty;
        a.water += (parseFloat(subSum.water_flow_gpm) || 0) * subQty;
        a.water_pd_max = Math.max(a.water_pd_max, parseFloat(subSum.water_pd_fth2o) || 0);
        a.heat_water += (parseFloat(subSum.heating_water_flow_gpm) || 0) * subQty;
        a.heat_water_pd_max = Math.max(a.heat_water_pd_max, parseFloat(subSum.heating_water_pd_fth2o) || 0);
      });
      return a;
    },
    { qty: 0, oa: 0, ea: 0, cool_total: 0, pa_sens: 0, pa_lat: 0, coil_sens: 0, coil_lat: 0, heat: 0, heat_pa: 0, water: 0, water_pd_max: 0, heat_water: 0, heat_water_pd_max: 0 }
  );

  // Column visibility: hide EA and Coil-Lat / PA-Lat / PA-Sens entirely when
  // no room has a non-zero value. Always show: Total Cooling, Coil Sens,
  // Water, PD. Heating is hidden when no room has heating demand AND no
  // heating-water temperature is configured in the project.
  const showEA = totals.ea > 0;
  const showPASens = totals.pa_sens > 0;
  const showPALat = totals.pa_lat > 0;
  const showCoilLat = totals.coil_lat > 0;
  const heatWaterTemp = parseFloat(
    project?.designConditions?.water_entering_heat_f
    ?? project?.designConditions?.water_entering_winter_f
  );
  const heatingWaterConfigured = Number.isFinite(heatWaterTemp) && heatWaterTemp > 0;
  const showHeating = totals.heat > 0 || heatingWaterConfigured;

  // When ALL rooms are PA (ACB / IU) models, the table layout is reordered to
  // a sensible-load focus per the product spec:
  //   Total Sens / PA Sens / Coil Sens / Latent / [Heating] / Water / PD / Delivered
  // For mixed or non-PA projects the original layout is used:
  //   Total Cooling / PA Sens / PA Lat / Coil Sens / Coil Lat / [Heating] / …
  const allPA = (rooms || []).length > 0 && rooms.every((r) => isPrimaryAir(r.unit_model));
  // Combined latent column = PA Lat + Coil Lat (relevant only when wet coil
  // i.e. project has latent loads).
  const showLatent = totals.pa_lat > 0 || totals.coil_lat > 0;

  // Compute room-level PER-UNIT values (one physical unit's selection).
  // Qty is purely a count for the BOM/schedule; it does not change the
  // displayed airflows/capacities. For multi-unit split rooms (>1 unit of
  // DIFFERENT lengths) the row still sums across the sub-units, because
  // those rooms have intentionally heterogeneous selections.
  const roomAggregate = (room, summary) => {
    const plan = summary.plan || {};
    const units = plan.units && plan.units.length
      ? plan.units
      : [{ qty: 1, per_unit_total: parseFloat(summary.cooling_total_btuh) || 0, per_unit_oa: parseFloat(summary.oa_cfm) || parseFloat(room.oa_flow_cfm) || 0, result: null }];
    // Detect true split (different lengths) — only then do we sum across units.
    const distinctLengths = new Set(units.map((u) => u.length_ft).filter((l) => l != null));
    const isSplit = units.length > 1 && distinctLengths.size > 1;
    const a = { oa: 0, ea: 0, cool_total: 0, coil_total: 0, pa_sens: 0, pa_lat: 0, coil_sens: 0, coil_lat: 0, heat: 0, heat_pa: 0, water: 0, water_pd: 0, heat_water: 0, heat_water_pd: 0 };
    units.forEach((u) => {
      // For non-split rooms (single selection, qty>1) → multiplier=1 so the
      // displayed values are per-unit selections regardless of qty.
      // For split rooms → use sub-unit qty so the row sums across distinct lengths.
      const sQty = isSplit ? (u.qty || 1) : 1;
      const sub = u.result?.summary || summary;
      const perOa = u.per_unit_oa != null
        ? parseFloat(u.per_unit_oa)
        : (parseFloat(sub.oa_cfm) || parseFloat(room.oa_flow_cfm) || 0);
      a.oa += perOa * sQty;
      a.ea += (parseFloat(sub.ea_cfm) || parseFloat(room.ea_flow_cfm) || 0) * sQty;
      a.cool_total += (parseFloat(sub.cooling_total_btuh) || parseFloat(u.per_unit_total) || 0) * sQty;
      // coil_total_btuh = Qc (coil sens + coil lat). Retained for completeness; Delivered % uses cool_total (Qr).
      const subCoilSens = parseFloat(sub.coil_sensible_btuh) || 0;
      const subCoilLat  = parseFloat(sub.coil_latent_btuh) || 0;
      const subCoilTotal = sub.coil_total_btuh != null
        ? (parseFloat(sub.coil_total_btuh) || 0)
        : (subCoilSens + subCoilLat);
      a.coil_total += subCoilTotal * sQty;
      a.pa_sens += (parseFloat(sub.pa_sensible_btuh) || 0) * sQty;
      a.pa_lat += (parseFloat(sub.pa_latent_btuh) || 0) * sQty;
      a.coil_sens += subCoilSens * sQty;
      a.coil_lat += subCoilLat * sQty;
      a.heat += (parseFloat(sub.heating_btuh) || 0) * sQty;
      a.heat_pa += (parseFloat(sub.heating_pa_btuh) || 0) * sQty;
      a.water += (parseFloat(sub.water_flow_gpm) || 0) * sQty;
      a.water_pd = Math.max(a.water_pd, parseFloat(sub.water_pd_fth2o) || 0);
      a.heat_water += (parseFloat(sub.heating_water_flow_gpm) || 0) * sQty;
      a.heat_water_pd = Math.max(a.heat_water_pd, parseFloat(sub.heating_water_pd_fth2o) || 0);
    });
    return a;
  };

  const renderRow = (e, highlightLabel) => {
    const { room, result, summary, idx } = e;
    const isRowLoading = rowLoading instanceof Set ? rowLoading.has(idx) : false;
    const displayName = modelDisplayName(room, result?.raw, isSI);
    const plan = summary.plan || {};
    const qty = plan.qty ?? 1;
    const target = parseFloat(room.cooling_total_btuh) || 0;
    // Flag when plan is undersized vs target (qty=1 case fell back to longest)
    const singleMax = (plan.units && plan.units.length === 1 ? plan.units[0].per_unit_total : plan.per_unit_total) || 0;
    const shortfall = target > 0 && singleMax > 0 ? (target - singleMax) / target : 0;
    const undersized = shortfall > 0.05;
    // True MIX: room has 2+ sub-units AND those sub-units have DIFFERENT
    // lengths (e.g., a room split into one 5 ft + one 3 ft via the
    // UnitSplitModal). A user simply changing the length of a single-unit
    // room is NOT a mix — that case is conveyed by highlighting the dropdown.
    const planUnits = plan.units || [];
    const distinctLengths = new Set(planUnits.map((u) => u.length_ft).filter((l) => l != null));
    const hasMixedLengths = planUnits.length > 1 && distinctLengths.size > 1;
    // User-picked length highlight (no MIX badge). Detected via the
    // `overridden` plan flag that onUpdateLength sets.
    const userPickedLength = plan.overridden === true && !hasMixedLengths;
    const agg = roomAggregate(room, summary);

    // Available lengths from the multi-length backend response. Suppressed
    // for Model N / N-f (fixed length) and when there is no `raw` (Model N
    // path uses a different shape). The dropdown lets the user pick a
    // different unit length post-selection without re-running the wizard.
    const lengths = (result?.ok && !isModelN(room.unit_model))
      ? lengthCapacityList(result?.raw, room.unit_model, project?.advanced?.piping, "room_load") // AHRI 1335 single-basis lock
      : [];
    const hasMultiCircuit = lengths.some((l) => l.circuits != null);
    const currentLen = plan?.length_ft ?? summary?.selected_length_ft ?? null;
    const currentCircuits = summary?.selected_circuits ?? null;
    const lengthSelectValue = currentLen != null
      ? (hasMultiCircuit ? `${currentLen}__${currentCircuits ?? ""}` : String(currentLen))
      : "";
    return (
      <TableRow
        key={`${room.id}-${highlightLabel || "main"}`}
        data-testid={`wizard-summary-row-${idx}`}
        className={`cursor-pointer hover:bg-muted/50 ${highlightLabel ? "bg-amber-50 dark:bg-amber-950/30" : qty === 0 ? "bg-amber-50 dark:bg-amber-900/10" : ""}`}
        onClick={() => result?.ok && setOpenIdx(idx)}
      >
        <TableCell className="font-mono">{idx + 1}</TableCell>
        <TableCell className="font-medium">
          {highlightLabel && (
            <span className="inline-flex items-center mr-1">
              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
            </span>
          )}
          {room.name || `Room ${idx + 1}`}
          {highlightLabel && (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
              {highlightLabel}
            </span>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1.5 flex-wrap">
            {lengths.length > 1 && onUpdateLength ? (
              <TooltipProvider delayDuration={150}>
                <Select
                  value={lengthSelectValue}
                  onValueChange={(v) => {
                    const [lenPart, cktPart] = v.split("__");
                    const newLen = parseFloat(lenPart);
                    const newCircuits = cktPart !== "" && cktPart !== undefined ? parseInt(cktPart, 10) : undefined;
                    if (!isNaN(newLen)) onUpdateLength(idx, newLen, newCircuits);
                  }}
                >
                  <SelectTrigger
                    className={`h-7 text-xs px-2 w-[130px] ${userPickedLength ? "border-emerald-500 ring-1 ring-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" : ""}`}
                    onClick={(ev) => ev.stopPropagation()}
                    data-testid={`wizard-summary-length-${idx}`}
                    title={userPickedLength ? "Length manually selected by user" : undefined}
                  >
                    <SelectValue placeholder={displayName} />
                  </SelectTrigger>
                  <SelectContent onClick={(ev) => ev.stopPropagation()}>
                    {lengths.map((l) => {
                      const itemVal = hasMultiCircuit
                        ? `${l.length_ft}__${l.circuits ?? ""}`
                        : String(l.length_ft);
                      return (
                        <SelectItem
                          key={itemVal}
                          value={itemVal}
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          <span className="flex items-center gap-1.5">
                            {`${room.unit_model}-${unitLengthLabel(l.length_ft, isSI)}${l.circuits != null ? ` (${l.circuits} ckt)` : ""} (max ${fmtKind(l.max_total, "capacity", isSI)})`}
                            {l.nozzle_warning && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>{l.nozzle_warning.message}</TooltipContent>
                              </Tooltip>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </TooltipProvider>
            ) : (
              <span>{displayName}</span>
            )}
            {summary.selected_circuits != null && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-muted text-muted-foreground font-medium cursor-default"
                      data-testid={`wizard-summary-circuits-${idx}`}
                    >
                      {summary.selected_circuits} ckt
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {summary.circuit_selection?.reason
                      ? summary.circuit_selection.reason
                      : `${summary.selected_circuits}-circuit configuration selected`}
                    {summary.circuit_selection?.alternative_circuits != null && (
                      <span className="block mt-0.5 text-muted-foreground">
                        Alternative: {summary.circuit_selection.alternative_circuits}-circuit available via Refine
                      </span>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </TableCell>
        <TableCell className="text-center font-semibold">
          <div className="flex items-center justify-center gap-1">
            {onUpdateQty ? (
              <QtyInput
                value={qty}
                onCommit={(v) => handleQtyCommit(idx, v)}
                testid={`wizard-summary-qty-${idx}`}
              />
            ) : (
              <span>{qty}</span>
            )}
            {undersized && (
              <span title={`Short ${(shortfall * 100).toFixed(1)}% of target`}>
                <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
              </span>
            )}
            {hasMixedLengths && (
              <span
                className="text-[9px] px-1 rounded bg-muted text-muted-foreground"
                title="Multiple units of different lengths in this room"
              >
                MIX
              </span>
            )}
            {qty === 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                Not in BOM
              </span>
            )}
          </div>
        </TableCell>
        {/* Column order (2026-06-24 per engineer): PA, [EA], Static P, Total
            Sens, Total Lat, Total, Water Cool, PD Cool, Heating, Water Heat,
            PD Heat, NC, Air Inlet, Delivered, Split. */}
        {/* PA — replaces OA column. For ACB rooms uses the editable PAFlowInput
            (PA = OA semantically); for non-ACB rooms shows the OA value
            read-only since FACB/EWS have no engineer-tunable PA stream. */}
        <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
          {isACB(room.unit_model) && onUpdateRoomPAFlow && result?.ok ? (
            <PAFlowInput
              value={parseFloat(room.oa_flow_cfm) || 0}
              isSI={isSI}
              disabled={isRowLoading}
              onCommit={(newCfm) => onUpdateRoomPAFlow(idx, newCfm)}
              testid={`wizard-summary-pa-flow-${idx}`}
              aria-label={`PA flow for ${room.name || `Room ${idx + 1}`}`}
            />
          ) : (
            <span className="cursor-default">{fmtKind(agg.oa, "flow", isSI)}</span>
          )}
        </TableCell>
        {showEA && <TableCell className="text-right">{fmtKind(agg.ea, "flow", isSI)}</TableCell>}
        {/* Static Pressure — inline editable for all CB-family rooms */}
        {hasCBFamilyRooms && (
          <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
            {isCBFamily(room.unit_model) ? (() => {
              const resolvedSp = getRoomStaticPressureInwg(room, result, project);
              const hasUserInput = (() => { const v = parseFloat(room?.pa_static_pressure_inwg); return Number.isFinite(v) && v > 0; })();
              const placeholderText = (!hasUserInput && resolvedSp != null)
                ? inputValue(resolvedSp, "staticPressure", isSI, isSI ? 0 : 3)
                : "—";
              return (
              <SPInput
                spInwg={room.pa_static_pressure_inwg}
                placeholder={placeholderText}
                isSI={isSI}
                disabled={isRowLoading}
                onCommit={(ipVal) => {
                  if (onUpdateRoomStaticPressure) {
                    onUpdateRoomStaticPressure(idx, ipVal === undefined || ipVal === null ? undefined : parseFloat(ipVal));
                  }
                }}
                testid={`wizard-summary-sp-${idx}`}
                aria-label={`Static pressure for ${room.name || `Room ${idx + 1}`}`}
              />
              );
            })() : (
              <span className="text-muted-foreground/50">—</span>
            )}
          </TableCell>
        )}
        {/* Total Sens / Total Lat / Total (consolidated PA + Coil). */}
        <TableCell className="text-right">{fmtKind((agg.pa_sens || 0) + (agg.coil_sens || 0), "capacity", isSI)}</TableCell>
        <TableCell className="text-right">{((agg.pa_lat || 0) + (agg.coil_lat || 0)) > 0 ? fmtKind((agg.pa_lat || 0) + (agg.coil_lat || 0), "capacity", isSI) : "—"}</TableCell>
        <TableCell className="text-right font-semibold">
          {fmtKind(agg.cool_total || ((agg.pa_sens || 0) + (agg.coil_sens || 0) + (agg.pa_lat || 0) + (agg.coil_lat || 0)), "capacity", isSI)}
        </TableCell>
        {/* Water Cool (GPM) — editable for CB-family rooms on blur/Enter */}
        <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
          {isCBFamily(room.unit_model) && onUpdateRoomWaterFlow && result?.ok ? (
            <WaterFlowInput
              value={agg.water}
              isSI={isSI}
              disabled={isRowLoading}
              onCommit={(newGpm) => onUpdateRoomWaterFlow(idx, newGpm)}
              testid={`wizard-summary-gpm-${idx}`}
              aria-label={`Water flow for ${room.name || `Room ${idx + 1}`}`}
            />
          ) : (
            <span onClick={(ev) => ev.stopPropagation()} className="cursor-default">{fmtKind(agg.water, "waterFlow", isSI)}</span>
          )}
        </TableCell>
        <TableCell className="text-right" title="Per-unit cooling pressure drop (units in parallel)">{fmtKind(agg.water_pd, "pressure", isSI)}</TableCell>
        {/* Heating (single column) — Net Heating = coil air-side + PA
            contribution. The "gross coil only" column was removed per the
            engineer's spec; this single value is what's delivered to the room. */}
        {showHeating && (
          <TableCell className="text-right font-semibold">
            {(agg.heat || 0) > 0 || (agg.heat_pa || 0) !== 0
              ? fmtKind((agg.heat || 0) + (agg.heat_pa || 0), "capacity", isSI)
              : "—"}
          </TableCell>
        )}
        {showHeating && <TableCell className="text-right">{agg.heat_water > 0 ? fmtKind(agg.heat_water, "waterFlow", isSI) : "—"}</TableCell>}
        {showHeating && <TableCell className="text-right" title="Per-unit heating pressure drop (units in parallel)">{agg.heat_water_pd > 0 ? fmtKind(agg.heat_water_pd, "pressure", isSI) : "—"}</TableCell>}
        {/* NC column — read-only integer from summary.nc_level (ACB rows). */}
        {hasACBRooms && (
          <TableCell className="text-center" onClick={(ev) => ev.stopPropagation()}>
            {isACB(room.unit_model) ? (() => {
              const nc = summary?.nc_level;
              if (nc == null) return <span className="text-muted-foreground/60 text-xs">—</span>;
              return <span className="font-mono tabular-nums text-xs font-semibold" data-testid={`wizard-summary-nc-${idx}`}>{Math.round(nc)}</span>;
            })() : (
              <span className="text-muted-foreground/50">—</span>
            )}
          </TableCell>
        )}
        {/* Air Inlet (Spigot) column — editable bore dropdown for ACB rows. */}
        {hasACBRooms && (
          <TableCell className="text-center" onClick={(ev) => ev.stopPropagation()}>
            {isACB(room.unit_model) && onUpdateRoomSpigot && result?.ok ? (
              <SpigotInput
                value={room.air_inlet_size != null
                  ? room.air_inlet_size
                  : (project?.advanced?.default_acb_spigot_in ?? 6)}
                disabled={isRowLoading}
                onCommit={(bore) => onUpdateRoomSpigot(idx, bore)}
                testid={`wizard-summary-spigot-${idx}`}
                aria-label={`Air inlet bore for ${room.name || `Room ${idx + 1}`}`}
              />
            ) : (
              <span className="text-muted-foreground/50">—</span>
            )}
          </TableCell>
        )}
        <TableCell>
          {result?.ok ? (() => {
            // Target resolution: prefer cooling_total_btuh, fall back to
            // cooling_sensible_btuh (ACB / IU products are sensible-load
            // focused — user often specifies sensible only).
            const tgtTot = parseFloat(room.cooling_total_btuh) || 0;
            const tgtSen = parseFloat(room.cooling_sensible_btuh) || 0;
            const usingSensible = !(tgtTot > 0) && tgtSen > 0;
            const tgt = tgtTot > 0 ? tgtTot : tgtSen;
            if (tgt <= 0) return <span className="text-muted-foreground/60 text-xs">—</span>;
            // For uniform-length rooms `agg.*` is PER UNIT (qty intentionally
            // Multiplier semantics:
            //   • DIVIDE mode (default): the engine sized each unit for
            //     room/qty + OA/qty. The sum across units is the actual
            //     delivered to the room → multiply per-unit delivered by qty.
            //   • DUPLICATE mode (Use same load & OA): each unit independently
            //     handles the FULL room load — adding qty doesn't add capacity
            //     to the room, it adds redundancy. Delivered % is per-unit vs
            //     room target.
            //   • SPLIT (mixed lengths): per-unit totals already aggregated.
            const planQty = plan.qty || 1;
            const planUnits = plan.units || [];
            const distinctLengths = new Set(planUnits.map((u) => u.length_ft).filter((l) => l != null));
            const isSplit = planUnits.length > 1 && distinctLengths.size > 1;
            const isDuplicate = plan.qty_mode === "duplicate";
            const mult = isSplit || isDuplicate ? 1 : planQty;
            // AHRI 1335 single-basis lock: Delivered % always uses Q_r (room-delivered).
            // water_side branch removed — agg.cool_total is always the correct value.
            const deliveredPerUnit = usingSensible
              ? (agg.pa_sens || 0) + (agg.coil_sens || 0)
              : agg.cool_total;
            const delivered = deliveredPerUnit * mult;
            const pct = (delivered / tgt - 1) * 100;
            const cls =
              pct > 200  ? "text-amber-600" :         // extreme over-delivery → likely wrong mode/qty
              pct >= -5  ? "text-emerald-600" :
              pct >= -10 ? "text-amber-600" :
              "text-rose-600";
            const sign = pct > 0 ? "+" : "";
            const showMultBadge = planQty > 1 && !isSplit && !isDuplicate;
            const dupNote = isDuplicate && planQty > 1
              ? ` (each of ${planQty} units handles full room load)`
              : "";
            const overDeliveryHint = pct > 200
              ? " — Over-delivery > 200%; consider switching to duplicate mode or reducing Qty."
              : "";
            return (
              <span className={`${cls} text-xs font-semibold`} title={`Delivered ${fmtKind(delivered, "capacity", isSI)} ${showMultBadge ? `(${planQty}×${fmtKind(deliveredPerUnit, "capacity", isSI)})` : ""} vs ${usingSensible ? "sensible " : ""}target ${fmtKind(tgt, "capacity", isSI)} ${unitLabel("capacity", isSI)}${dupNote}${overDeliveryHint}`}>
                {sign}{pct.toFixed(1)}%
              </span>
            );
          })() : (
            <span className="text-rose-600 text-xs" title={result?.error}>
              Failed
            </span>
          )}
        </TableCell>
        <TableCell onClick={(ev) => ev.stopPropagation()}>
          {result?.ok && onUpdatePlan && !highlightLabel && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setSplitIdx(idx)}
              data-testid={`edit-split-btn-${idx}`}
            >
              <Settings2 className="h-3 w-3 mr-1" /> Split
            </Button>
          )}
        </TableCell>
        {/* Per-row loading spinner — last cell, matched by the empty header above */}
        <TableCell className="w-6 px-1">
          {isRowLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Recalculating…" />}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-4" data-testid="wizard-summary-tab">
      <Card className="print:shadow-none print:border-0">
        <CardHeader className="flex flex-row items-center justify-between print:px-0">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ListChecks className="h-5 w-5 text-muted-foreground" />
            Selection Summary
          </CardTitle>
          <div className="flex items-center gap-3 print:hidden">
            {!isControlled && (
              <div className="flex items-center gap-2 text-xs">
                <Label htmlFor="si-toggle" className="text-xs">IP</Label>
                <Switch
                  id="si-toggle"
                  checked={isSI}
                  onCheckedChange={setIsSI}
                  data-testid="wizard-summary-si-toggle"
                />
                <Label htmlFor="si-toggle" className="text-xs">SI</Label>
              </div>
            )}
            {/* Fill SP — bulk-set pa_static_pressure_inwg on all ACB rooms */}
            {hasACBRooms && onUpdateRoomStaticPressure && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setFillSPValue(""); setFillSPOpen(true); }}
                data-testid="wizard-summary-fill-sp"
              >
                Fill SP
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="wizard-summary-print">
              <Printer className="h-4 w-4 mr-1" /> Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveSchedule}
              disabled={savingBusy || resultsStale}
              title={resultsStale ? "Re-run Selection first — results are stale" : undefined}
              data-testid="wizard-summary-save"
            >
              <Save className="h-4 w-4 mr-1" />
              {savingBusy ? "Saving…" : "Save Schedule"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportUnitSchedule}
              disabled={exportBusy || resultsStale}
              title={resultsStale ? "Re-run Selection first — results are stale" : undefined}
              data-testid="wizard-summary-pdf"
            >
              {exportBusy ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-1" />
              )}
              Unit Schedule
            </Button>
            <Button
              size="sm"
              onClick={handleSubmittal}
              disabled={submittalBusy || resultsStale}
              title={resultsStale ? "Re-run Selection first — results are stale" : undefined}
              data-testid="wizard-summary-submittal"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {submittalBusy ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-1" />
              )}
              {selectionReport ? "Re-generate Selection Report" : "Selection Report"}
            </Button>
            {selectionReport && (
              <span
                className="text-[11px] text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 px-2 py-1 rounded inline-flex items-center gap-1 self-center"
                data-testid="selection-report-status"
                title={`Generated ${new Date(selectionReport.generated_at).toLocaleString()} · ${(selectionReport.size_bytes / 1024).toFixed(0)} KB`}
              >
                ✓ Saved {new Date(selectionReport.generated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenDetailPicker}
              disabled={resultsStale || !results || results.length === 0}
              title={resultsStale ? "Re-run Selection first — results are stale" : "Generate a detailed per-room PDF (pick rooms, batched to avoid memory exhaustion)"}
              data-testid="wizard-summary-detailed-report"
            >
              <ListChecks className="h-4 w-4 mr-1" />
              Detailed Report
            </Button>
          </div>
        </CardHeader>
        <CardContent className="print:px-0">
          {/* Stale-results banner: shown when design conditions, advanced
              settings, or rooms changed after the last Selection run. */}
          {resultsStale && results && results.length > 0 && (
            <div
              className="mb-4 p-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 print:hidden flex items-center justify-between gap-3 flex-wrap"
              data-testid="wizard-summary-stale-banner"
            >
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>Design conditions or room data changed since last selection. Re-run Selection to update.</span>
              </div>
              {onRerunSelection && (
                <Button
                  size="sm"
                  onClick={onRerunSelection}
                  disabled={running}
                  data-testid="wizard-summary-stale-rerun"
                  className="bg-amber-700 text-white hover:bg-amber-800 border-0 shrink-0"
                >
                  {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Re-run Selection
                </Button>
              )}
            </div>
          )}
          {/* Initial-pass workflow bar: shown after first calculation so the
              user can choose to Accept / Optimize / Individual. Disappears
              once the user accepts the selection. */}
          {selectionMode !== "accepted" && results && results.length > 0 && onOptimize && (
            <div className="mb-4 p-3 rounded-md border border-border bg-muted print:hidden flex items-center justify-between gap-3 flex-wrap" data-testid="wizard-summary-workflow-bar">
              <div className="text-xs text-foreground">
                {selectionMode === "initial" && (
                  <>
                    <strong>Initial selection complete.</strong> Each unit was sized at the given PA flow, max water flow, and max length cap (per Advanced tab). Review the Delivered % column and choose next, then press <strong>Next</strong> above to save the schedule:
                  </>
                )}
                {selectionMode === "optimized" && (
                  <span className="text-emerald-700 dark:text-emerald-400"><strong>Optimized.</strong> PA flow + length escalation applied to under-delivering rooms. Press <strong>Next</strong> above to save the schedule.</span>
                )}
                {selectionMode === "individual" && (
                  <span className="text-indigo-700 dark:text-indigo-400"><strong>Individual mode.</strong> Use the length dropdown next to each unit to pick manually, then press <strong>Next</strong> above to save the schedule.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={onAccept}
                  disabled={running}
                  data-testid="wizard-summary-accept"
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onOptimize}
                  disabled={running}
                  data-testid="wizard-summary-optimize"
                >
                  {running ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Optimize
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onIndividual}
                  disabled={running}
                  data-testid="wizard-summary-individual"
                >
                  Individual
                </Button>
              </div>
            </div>
          )}
          {selectionMode === "accepted" && (
            <div className="mb-4 p-2 rounded bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-xs text-emerald-700 dark:text-emerald-400 print:hidden">
              ✓ Selection accepted. Use Selection Report above to generate the submittal PDF.
            </div>
          )}
          {/* B.1.5 — Active settings chips: Capacity Basis + per-family Strategy */}
          {/* AHRI 1335 single-basis lock: basis chip always shows Q_r. */}
          {(() => {
            const facbStrategy = project.facb_strategy || "lowest_speed";
            const acbStrategy = project.acb_strategy || "given_flow_given_sp";

            const basisLabel = "Q_r — AHRI 1335 Room Load";
            const basisTooltip = "Capacity basis locked to AHRI 1335 air-side room-delivered (Q_r). Water-side plant-sizing data is available in the per-room Water Side section.";

            const { hasFACB, hasACB } = getProjectFamilies(rooms);

            const facbLabel = facbStrategy === "given_flow"
              ? "FACB: Given water flow"
              : "FACB: Lowest fan speed";
            const facbTooltip = facbStrategy === "given_flow"
              ? "FACB (S, S-HW, O-1, O-2) — Engine holds the water flow fixed and finds the shortest length that meets the target. Speed still cascades LOW→MED→HIGH."
              : "FACB (S, S-HW, O-1, O-2) — Engine walks LOW→MED→HIGH; water flow is modulated within the max cap to meet the target.";

            const ACB_CHIP_LABELS = {
              given_flow_given_sp: "ACB: Given PA + given SP",
              given_flow_lowest_sp: "ACB: Given PA + lowest SP",
              lowest_flow_given_sp: "ACB: Lowest PA + given SP",
              lowest_flow_lowest_sp: "ACB: Lowest PA + lowest SP",
            };
            const ACB_CHIP_TOOLTIPS = {
              given_flow_given_sp: "ACB (I, L, C, C1, H) — Given PA flow at given static pressure.",
              given_flow_lowest_sp: "ACB (I, L, C, C1, H) — Given PA flow for lowest static pressure.",
              lowest_flow_given_sp: "ACB (I, L, C, C1, H) — Lowest PA flow at given static pressure.",
              lowest_flow_lowest_sp: "ACB (I, L, C, C1, H) — Lowest PA flow and lowest static pressure.",
            };
            const acbLabel = ACB_CHIP_LABELS[acbStrategy] || ACB_CHIP_LABELS["given_flow_given_sp"];
            const acbTooltip = ACB_CHIP_TOOLTIPS[acbStrategy] || ACB_CHIP_TOOLTIPS["given_flow_given_sp"];

            return (
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="wizard-summary-settings-chips">
                {/* Capacity Basis — always shown */}
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="cursor-default flex items-center gap-1 text-xs"
                        data-testid="wizard-summary-chip-basis"
                      >
                        <Info className="h-3 w-3 text-muted-foreground" aria-hidden />
                        {basisLabel}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-xs">
                      {basisTooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* FACB Strategy chip — shown only when FACB rooms exist */}
                {hasFACB && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          variant="outline"
                          className="cursor-default flex items-center gap-1 text-xs"
                          data-testid="wizard-summary-chip-facb-strategy"
                        >
                          <Info className="h-3 w-3 text-muted-foreground" aria-hidden />
                          {facbLabel}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {facbTooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* ACB Strategy chip — shown only when ACB rooms exist */}
                {hasACB && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge
                          variant="outline"
                          className="cursor-default flex items-center gap-1 text-xs"
                          data-testid="wizard-summary-chip-acb-strategy"
                        >
                          <Info className="h-3 w-3 text-muted-foreground" aria-hidden />
                          {acbLabel}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {acbTooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            );
          })()}

          {/* Project header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs border-b border-border pb-3">
            <div>
              <div className="text-muted-foreground">Project Name</div>
              <div className="font-semibold text-foreground">{project.name || "—"}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Project Number</div>
              <div className="font-semibold text-foreground">{project.number || "—"}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Address</div>
              <div className="font-semibold text-foreground">{project.address || "—"}</div>
            </div>
            <div>
              <div className="text-muted-foreground">City / State</div>
              <div className="font-semibold text-foreground">
                {[project.cityText, project.state].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
          </div>

          {/* "Set all units to same length" toolbar — global + per-model.
              Helps avoid manually changing each room when bumping lengths. */}
          {onUpdateLength && (() => {
            // Build available length lists per (model). We intersect lengths
            // across all rooms of that model, so picking "set all → X ft"
            // only offers values that work for every room (max-length cap +
            // backend response support both apply).
            const byModel = new Map();
            enriched.forEach((e) => {
              const m = e.room?.unit_model;
              if (!m || isModelN(m)) return;
              if (!e.result?.ok) return;
              const lcl = lengthCapacityList(
                e.result?.raw, m, project?.advanced?.piping, "room_load" // AHRI 1335 single-basis lock
              );
              const ls = lcl.map((x) => x.length_ft);
              if (!ls.length) return;
              if (!byModel.has(m)) {
                // warnedLengths tracks which length_ft values have a warning in ANY room.
                const warnedLengths = new Set(
                  lcl.filter((x) => x.nozzle_warning).map((x) => x.length_ft)
                );
                byModel.set(m, { lengths: new Set(ls), rooms: [], warnedLengths });
              } else {
                // Intersect (only keep lengths that EVERY room of this model supports)
                const cur = byModel.get(m).lengths;
                byModel.get(m).lengths = new Set(
                  [...cur].filter((x) => ls.includes(x))
                );
                // Union: a length is warned if ANY room has a warning for it.
                lcl.filter((x) => x.nozzle_warning).forEach((x) =>
                  byModel.get(m).warnedLengths.add(x.length_ft)
                );
              }
              byModel.get(m).rooms.push(e.idx);
            });
            // Keep only models with ≥2 rooms and ≥1 viable length.
            const eligible = [...byModel.entries()]
              .filter(([, v]) => v.rooms.length > 1 && v.lengths.size > 0)
              .map(([model, v]) => ({
                model,
                rooms: v.rooms,
                lengths: [...v.lengths].sort((a, b) => a - b),
                warnedLengths: v.warnedLengths,
              }));
            if (!eligible.length) return null;

            // Universal "set all" (intersection across ALL models).
            const allLens = eligible.length === 1
              ? eligible[0].lengths
              : eligible.reduce(
                (acc, e) => acc.filter((l) => e.lengths.includes(l)),
                eligible[0].lengths
              );
            // Union of warned lengths across all eligible models for the global picker.
            const allWarnedLengths = new Set(
              eligible.flatMap((e) => [...e.warnedLengths])
            );

            const setAllRoomsTo = (roomsList, newLen) => {
              roomsList.forEach((idx) => onUpdateLength(idx, newLen));
              const lenLbl = isSI ? `${Math.round(newLen * 300)} mm` : `${newLen} ft`;
              toast.success(`Set ${roomsList.length} room${roomsList.length !== 1 ? "s" : ""} to ${lenLbl}`);
            };

            return (
              <TooltipProvider delayDuration={150}>
                <div className="mb-3 flex flex-wrap items-center gap-2 p-2 rounded bg-muted border border-border text-xs print:hidden">
                  <span className="font-semibold text-foreground">Bulk length:</span>
                  {/* Global picker — only render if intersection is non-empty AND there are 2+ models */}
                  {allLens.length > 0 && eligible.length > 1 && (
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">All units →</span>
                      <Select
                        value=""
                        onValueChange={(v) => {
                          const newLen = parseFloat(v);
                          if (!isNaN(newLen)) {
                            const allRoomIdx = eligible.flatMap((e) => e.rooms);
                            setAllRoomsTo(allRoomIdx, newLen);
                          }
                        }}
                      >
                        <SelectTrigger
                          className="h-7 text-xs px-2 w-[130px]"
                          data-testid="wizard-summary-bulk-length-all"
                        >
                          <SelectValue placeholder="Pick length…" />
                        </SelectTrigger>
                        <SelectContent>
                          {allLens.map((l) => (
                            <SelectItem key={l} value={String(l)}>
                              <span className="flex items-center gap-1.5">
                                {isSI ? `${Math.round(l * 300)} mm` : `${l} ft`}
                                {allWarnedLengths.has(l) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>Nozzle undersized for at least one room at this length</TooltipContent>
                                  </Tooltip>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {/* Per-model pickers */}
                  {eligible.map((e) => (
                    <div className="flex items-center gap-1" key={e.model}>
                      <span className="text-muted-foreground">{e.model} ({e.rooms.length}) →</span>
                      <Select
                        value=""
                        onValueChange={(v) => {
                          const newLen = parseFloat(v);
                          if (!isNaN(newLen)) setAllRoomsTo(e.rooms, newLen);
                        }}
                      >
                        <SelectTrigger
                          className="h-7 text-xs px-2 w-[120px]"
                          data-testid={`wizard-summary-bulk-length-${e.model}`}
                        >
                          <SelectValue placeholder="Pick length…" />
                        </SelectTrigger>
                        <SelectContent>
                          {e.lengths.map((l) => (
                            <SelectItem key={l} value={String(l)}>
                              <span className="flex items-center gap-1.5">
                                {isSI ? `${Math.round(l * 300)} mm` : `${l} ft`}
                                {e.warnedLengths.has(l) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex">
                                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>Nozzle undersized for at least one room at this length</TooltipContent>
                                  </Tooltip>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </TooltipProvider>
            );
          })()}

          <div className="overflow-x-auto">
          <Table className="text-xs min-w-full" data-testid="wizard-summary-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">No</TableHead>
                <TableHead>Room Name</TableHead>
                <TableHead>Unit Model</TableHead>
                <TableHead className="text-center">Qty</TableHead>
                {/* Column order (2026-06-24 per engineer):
                    PA, [EA], Static P, Total Sens, Total Lat, Total, Water Cool,
                    PD Cool, Heating, Water Heat, PD Heat, NC, Air Inlet,
                    Delivered, Split. PA replaces OA (same value semantically
                    for ACB; editable PA-flow input on ACB rooms). */}
                <TableHead className="text-right" title="Primary Air flow — editable on ACB rooms; OA value on FACB/EWS">PA ({unitLabel("flow", isSI)})</TableHead>
                {showEA && <TableHead className="text-right">EA ({unitLabel("flow", isSI)})</TableHead>}
                {hasCBFamilyRooms && <TableHead className="text-right" title="PA static pressure — editable, CB-family rooms">Static P ({unitLabel("staticPressure", isSI)})</TableHead>}
                <TableHead className="text-right" title="Total sensible (PA + Coil)">Total Sens ({unitLabel("capacity", isSI)})</TableHead>
                <TableHead className="text-right" title="Total latent (PA + Coil)">Total Lat ({unitLabel("capacity", isSI)})</TableHead>
                <TableHead className="text-right" title="Total cooling capacity (sensible + latent)">Total ({unitLabel("capacity", isSI)})</TableHead>
                <TableHead className="text-right" title="Cooling water flow">Water Cool ({unitLabel("waterFlow", isSI)})</TableHead>
                <TableHead className="text-right" title="Cooling-side pressure drop">PD Cool ({unitLabel("pressure", isSI)})</TableHead>
                {showHeating && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <TableHead className="text-right cursor-default">Heating ({unitLabel("capacity", isSI)})</TableHead>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        Coil air-side + Primary Air contribution = total heat delivered to the room.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {showHeating && <TableHead className="text-right" title="Heating water flow">Water Heat ({unitLabel("waterFlow", isSI)})</TableHead>}
                {showHeating && <TableHead className="text-right" title="Heating-side pressure drop">PD Heat ({unitLabel("pressure", isSI)})</TableHead>}
                {hasACBRooms && <TableHead className="text-center" title="Noise Criterion (NC). Computed from primary air flow, static pressure, and spigot bore.">NC</TableHead>}
                {hasACBRooms && <TableHead className="text-center" title='Spigot / air-inlet bore. NC depends on this for Model C/C1/D; flow-only ACBs (H, I, L) ignore it.'>Air Inlet</TableHead>}
                <TableHead title="Delivered total cooling vs required (+ above target, − below). Green: ≥−5%, Amber: −5% to −10%, Red: < −10%.">Delivered</TableHead>
                <TableHead>Split</TableHead>
                <TableHead className="w-6 px-1" aria-hidden />
              </TableRow>
            </TableHeader>
            <TableBody>
              {enriched.map((e) => renderRow(e))}
              {/* Totals row */}
              <TableRow className="bg-muted font-semibold">
                <TableCell colSpan={3}>Project Totals (sum across rooms)</TableCell>
                <TableCell className="text-center">{totals.qty}</TableCell>
                {/* Mirror the header order: PA, [EA], Static, Total Sens, Total
                    Lat, Total, Water Cool, PD Cool, [Heating, Water Heat, PD
                    Heat], [NC, Air Inlet], Delivered, Split, spinner. */}
                <TableCell className="text-right">{fmtKind(totals.oa, "flow", isSI)}</TableCell>
                {showEA && <TableCell className="text-right">{fmtKind(totals.ea, "flow", isSI)}</TableCell>}
                {hasCBFamilyRooms && <TableCell></TableCell>}{/* Static P */}
                <TableCell className="text-right">{fmtKind((totals.pa_sens || 0) + (totals.coil_sens || 0), "capacity", isSI)}</TableCell>
                <TableCell className="text-right">{((totals.pa_lat || 0) + (totals.coil_lat || 0)) > 0 ? fmtKind((totals.pa_lat || 0) + (totals.coil_lat || 0), "capacity", isSI) : "—"}</TableCell>
                <TableCell className="text-right font-semibold">
                  {fmtKind(totals.cool_total || ((totals.pa_sens || 0) + (totals.coil_sens || 0) + (totals.pa_lat || 0) + (totals.coil_lat || 0)), "capacity", isSI)}
                </TableCell>
                <TableCell className="text-right">{fmtKind(totals.water, "waterFlow", isSI)}</TableCell>
                <TableCell className="text-right text-xs text-muted-foreground" title="Max cooling PD across rooms">
                  max {fmtKind(totals.water_pd_max, "pressure", isSI)}
                </TableCell>
                {showHeating && (
                  <TableCell className="text-right font-semibold">
                    {(totals.heat || 0) > 0 || (totals.heat_pa || 0) !== 0
                      ? fmtKind((totals.heat || 0) + (totals.heat_pa || 0), "capacity", isSI)
                      : "—"}
                  </TableCell>
                )}
                {showHeating && <TableCell className="text-right">{totals.heat_water > 0 ? fmtKind(totals.heat_water, "waterFlow", isSI) : "—"}</TableCell>}
                {showHeating && (
                  <TableCell className="text-right text-xs text-muted-foreground" title="Max heating PD across rooms">
                    {totals.heat_water_pd_max > 0 ? `max ${fmtKind(totals.heat_water_pd_max, "pressure", isSI)}` : "—"}
                  </TableCell>
                )}
                {hasACBRooms && <TableCell></TableCell>}{/* NC */}
                {hasACBRooms && <TableCell></TableCell>}{/* Air Inlet */}
                <TableCell></TableCell>{/* Delivered */}
                <TableCell></TableCell>{/* Split */}
                <TableCell></TableCell>{/* spinner */}
              </TableRow>
            </TableBody>
          </Table>
          </div>

          {/* Click-to-detail hint */}
          {enriched.some((e) => e.result?.ok) && (
            <div className="mt-2 text-[11px] text-muted-foreground">
              Click any row to view detailed results, capacity tables, and psychrometric chart.
            </div>
          )}

          {/* Errors */}
          {results.some((r) => r && !r.ok) && (
            <div className="mt-4 text-xs">
              <div className="font-semibold text-rose-600 mb-1">Errors:</div>
              <ul className="list-disc pl-5 text-rose-700">
                {results.map((r, i) =>
                  r && !r.ok ? (
                    <li key={i}>
                      Room {i + 1} ({rooms[i]?.name || "—"}): {r.error}
                    </li>
                  ) : null
                )}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Engineer's Notes — per room review panel. Positioned here so the
          engineer can annotate before generating any PDF. Notes persist via
          onUpdateRoomNotes → setRooms in ProjectWizard → auto-save. */}
      {rooms && rooms.length > 0 && (
        <Collapsible
          open={notesOpen}
          onOpenChange={setNotesOpen}
          data-testid="engineers-notes-review"
          className="print:hidden"
        >
          <div className="border border-border rounded overflow-hidden">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between px-4 py-3 bg-muted hover:bg-muted/80 transition-colors text-sm font-semibold text-foreground border-b border-border"
                data-testid="engineers-notes-trigger"
              >
                <span>Engineer&rsquo;s Notes — per room</span>
                <div className="flex items-center gap-2">
                  {rooms.some((r) => (r.notes ?? "").trim()) && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-normal">
                      {rooms.filter((r) => (r.notes ?? "").trim()).length} with notes
                    </span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${notesOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-4 space-y-3 bg-card">
                {rooms.map((room, idx) => {
                  const result = results[idx];
                  const displayName = modelDisplayName(room, result?.raw, isSI);
                  return (
                    <div key={room.id || idx} className="grid grid-cols-[minmax(160px,1fr)_3fr] gap-3 items-start border-b border-border/50 pb-3 last:border-b-0 last:pb-0">
                      <div className="pt-1">
                        <div className="text-xs font-semibold text-foreground truncate" title={room.name || `Room ${idx + 1}`}>
                          {room.name || `Room ${idx + 1}`}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate">{displayName}</div>
                      </div>
                      <Textarea
                        value={room.notes ?? ""}
                        onChange={(e) => {
                          if (onUpdateRoomNotes) onUpdateRoomNotes(idx, e.target.value);
                        }}
                        placeholder="Optional notes for this room (appears in PDF report)"
                        className="text-xs min-h-[48px] max-h-[120px] resize-y border-border bg-background font-sans"
                        data-testid={`engineers-notes-room-${idx}`}
                        aria-label={`Notes for ${room.name || `Room ${idx + 1}`}`}
                      />
                    </div>
                  );
                })}
                {rooms.length > 1 && (
                  <div className="flex justify-end pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-muted-foreground hover:text-foreground h-7 px-2"
                      data-testid="engineers-notes-clear-all"
                      onClick={() => {
                        if (!onUpdateRoomNotes) return;
                        // eslint-disable-next-line no-restricted-globals
                        if (!window.confirm("Clear all room notes?")) return;
                        rooms.forEach((_, i) => onUpdateRoomNotes(i, ""));
                      }}
                    >
                      Bulk: clear all notes
                    </Button>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {/* Highest-capacity / OA / heating extremes */}
      {extremes && (
        <Card className="print:shadow-none print:border-0" data-testid="wizard-summary-extremes">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
              Critical Rooms (size selection here)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">No</TableHead>
                  <TableHead>Room Name</TableHead>
                  <TableHead>Unit Model</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead className="text-right">OA ({unitLabel("flow", isSI)})</TableHead>
                  {showEA && <TableHead className="text-right">EA ({unitLabel("flow", isSI)})</TableHead>}
                  {/* Per-user spec: Total / Total Sens / Total Lat */}
                  <TableHead className="text-right">Total ({unitLabel("capacity", isSI)})</TableHead>
                  <TableHead className="text-right">Total Sens ({unitLabel("capacity", isSI)})</TableHead>
                  <TableHead className="text-right">Total Lat ({unitLabel("capacity", isSI)})</TableHead>
                  {showHeating && <TableHead className="text-right">Heating ({unitLabel("capacity", isSI)})</TableHead>}
                  {showHeating && (
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <TableHead className="text-right cursor-default">
                            Net Heating ({unitLabel("capacity", isSI)})
                          </TableHead>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-xs">
                          Coil air-side + Primary Air contribution = total heat delivered to the room. Compare this against the room&apos;s heating load target.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <TableHead className="text-right" title="Cooling water flow">Water Cool ({unitLabel("waterFlow", isSI)})</TableHead>
                  <TableHead className="text-right" title="Cooling-side pressure drop">PD Cool ({unitLabel("pressure", isSI)})</TableHead>
                  {showHeating && <TableHead className="text-right" title="Heating water flow">Water Heat ({unitLabel("waterFlow", isSI)})</TableHead>}
                  {showHeating && <TableHead className="text-right" title="Heating-side pressure drop">PD Heat ({unitLabel("pressure", isSI)})</TableHead>}
                  <TableHead title="Delivered total cooling vs required (+ above target, − below)">Delivered</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {extremes.capacity && renderRow(extremes.capacity, "Highest Cooling")}
                {extremes.oa &&
                  extremes.oa !== extremes.capacity &&
                  renderRow(extremes.oa, "Highest OA")}
                {extremes.heating &&
                  parseFloat(extremes.heating.summary.heating_btuh) > 0 &&
                  extremes.heating !== extremes.capacity &&
                  extremes.heating !== extremes.oa &&
                  renderRow(extremes.heating, "Highest Heating")}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Unit Split / Override Modal */}
      {splitIdx !== null && (
        <UnitSplitModal
          open={splitIdx !== null}
          onOpenChange={(o) => !o && setSplitIdx(null)}
          project={project}
          room={rooms[splitIdx]}
          plan={results[splitIdx]?.summary?.plan}
          onApply={(newUnits) => {
            if (onUpdatePlan) onUpdatePlan(splitIdx, newUnits);
          }}
        />
      )}

      {/* Schedule export options dialog removed — the "Unit Schedule"
          button now exports both XLSX + CSV directly without a chooser. */}

      {/* QTY-mode picker — asks the engineer whether the room load should be
          divided across the QTY units (engine sizes each unit for room/qty)
          or duplicated (each unit handles full room load + full OA). */}
      <Dialog
        open={qtyModeAsk !== null}
        onOpenChange={(o) => !o && setQtyModeAsk(null)}
      >
        <DialogContent
          data-testid="qty-mode-dialog"
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>How should QTY {qtyModeAsk?.qty || ""} be applied?</DialogTitle>
            <DialogDescription>
              You're setting <strong>{qtyModeAsk?.qty || ""} units</strong> for
              {" "}
              <span className="font-medium text-foreground">
                {qtyModeAsk?.idx != null ? (rooms[qtyModeAsk.idx]?.name || `Room ${qtyModeAsk.idx + 1}`) : ""}
              </span>.
              Choose how the room load and outside air should be split.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <button
              type="button"
              data-testid="qty-mode-divide"
              onClick={() => handlePickQtyMode("divide")}
              className="w-full text-left p-3 rounded border border-border hover:border-foreground hover:bg-muted transition-colors"
            >
              <div className="font-semibold text-foreground text-sm">Divide load &amp; OA across units</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Each unit sizes for{" "}
                <span className="font-mono">load ÷ {qtyModeAsk?.qty || ""}</span> and{" "}
                <span className="font-mono">OA ÷ {qtyModeAsk?.qty || ""}</span>.
                Use this when the {qtyModeAsk?.qty || ""} units jointly serve the room.
              </div>
            </button>
            <button
              type="button"
              data-testid="qty-mode-duplicate"
              onClick={() => handlePickQtyMode("duplicate")}
              className="w-full text-left p-3 rounded border border-border hover:border-foreground hover:bg-muted transition-colors"
            >
              <div className="font-semibold text-foreground text-sm">Use the same load &amp; OA for each unit</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Each unit independently handles the <span className="font-mono">FULL room load</span> and
                <span className="font-mono"> FULL OA</span>. Use this for parallel zones, redundancy,
                or when each unit serves the same room independently.
              </div>
            </button>
            {/* Apply-to-all toggle. Persists the chosen mode on the project so
                subsequent QTY > 1 commits across any room use it silently. */}
            <label
              className="flex items-start gap-2 pt-2 cursor-pointer"
              data-testid="qty-mode-apply-all-label"
            >
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-foreground"
                data-testid="qty-mode-apply-all"
              />
              <span className="text-xs text-muted-foreground leading-snug">
                <span className="font-medium text-foreground">Apply this choice to all rooms in this project</span>
                <br />
                Future QTY changes &gt; 1 will silently use the chosen mode for every room. Untick to decide per room.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setQtyModeAsk(null)}
              data-testid="qty-mode-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* B.1.6 — Fill SP dialog: bulk-set pa_static_pressure_inwg on all ACB rooms */}
      <Dialog open={fillSPOpen} onOpenChange={(o) => !o && setFillSPOpen(false)}>
        <DialogContent data-testid="fill-sp-dialog" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Fill Static Pressure</DialogTitle>
            <DialogDescription>
              Set a single PA static pressure value on all ACB rooms in this project.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label
              htmlFor="fill-sp-input"
              className="block text-xs font-medium mb-1"
            >
              Static pressure (inWG)
            </label>
            <input
              id="fill-sp-input"
              type="number"
              step="0.01"
              min="0"
              className="h-8 w-full rounded border border-input bg-background px-2 text-xs font-mono tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="e.g. 0.05"
              value={fillSPValue}
              onChange={(e) => setFillSPValue(e.target.value)}
              data-testid="fill-sp-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFillSPOpen(false)}
              data-testid="fill-sp-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="fill-sp-apply"
              onClick={() => {
                const val = fillSPValue === "" ? undefined : parseFloat(fillSPValue);
                if (onUpdateRoomStaticPressure) {
                  (rooms || []).forEach((r, i) => {
                    if (isACB(r.unit_model)) {
                      onUpdateRoomStaticPressure(i, isNaN(val) ? undefined : val);
                    }
                  });
                }
                setFillSPOpen(false);
              }}
            >
              Apply to all ACB rooms
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detailed Report Picker — room selector + progress bar for batched PDF */}
      <Dialog
        open={detailPickerOpen}
        onOpenChange={(o) => { if (!detailBusy) setDetailPickerOpen(o); }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col" data-testid="detail-report-dialog">
          <DialogHeader>
            <DialogTitle>Detailed Report — Select Rooms</DialogTitle>
            <DialogDescription>
              Each selected room will be rendered as a full detail page.
              For large projects, processing runs in batches to prevent memory exhaustion.
            </DialogDescription>
          </DialogHeader>

          {/* Quick-select bar */}
          <div className="flex gap-2 flex-wrap pb-2 border-b border-border">
            <Button variant="outline" size="sm" onClick={handleDetailSelectAll} disabled={detailBusy}>
              Select all
            </Button>
            <Button variant="outline" size="sm" onClick={handleDetailSelectNone} disabled={detailBusy}>
              Select none
            </Button>
            <Button variant="outline" size="sm" onClick={handleDetailSelectIssues} disabled={detailBusy}>
              Issues only (&lt;95% or &gt;110%)
            </Button>
            <span className="ml-auto self-center text-xs text-muted-foreground">
              {detailSelectedIdxs.length} / {rooms?.length || 0} selected
            </span>
          </div>

          {/* Room checkbox list */}
          <div className="overflow-y-auto flex-1 min-h-0 space-y-1 py-1 pr-1">
            {(rooms || []).map((room, idx) => {
              const res = results?.[idx];
              const isOk = res?.ok;
              const s = res?.summary || {};
              const modelName = room.unit_model || "—";
              const plan = s.plan || {};
              const planQty = plan.qty || 1;
              const planUnits = plan.units || [];
              const distinctLengths = new Set(planUnits.map((u) => u.length_ft).filter((l) => l != null));
              const isSplit = planUnits.length > 1 && distinctLengths.size > 1;
              const isDuplicate = plan.qty_mode === "duplicate";
              const tgt = parseFloat(room.cooling_total_btuh) || parseFloat(room.cooling_sensible_btuh) || 0;
              const mult = (isSplit || isDuplicate) ? 1 : planQty;
              const delivered = (parseFloat(s.cooling_total_btuh) || parseFloat(s.coil_sensible_btuh) || 0) * mult;
              const pct = tgt > 0 ? (delivered / tgt) * 100 : null;
              const pctColor =
                pct === null ? "" :
                pct >= 95 && pct <= 110 ? "text-emerald-700 dark:text-emerald-400" :
                pct >= 90 ? "text-amber-700 dark:text-amber-400" :
                "text-red-700 dark:text-red-400";

              return (
                <label
                  key={idx}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={detailSelectedIdxs.includes(idx)}
                    onCheckedChange={() => handleDetailToggleRoom(idx)}
                    disabled={detailBusy || !isOk}
                    id={`detail-room-${idx}`}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="font-medium truncate">{room.name || `Room ${idx + 1}`}</span>
                    <span className="text-muted-foreground ml-1.5 text-xs">{modelName}</span>
                  </span>
                  {!isOk && (
                    <span className="text-xs text-red-600 dark:text-red-400 shrink-0">FAIL</span>
                  )}
                  {isOk && pct !== null && (
                    <span className={`text-xs font-medium shrink-0 ${pctColor}`}>
                      {pct >= 0 ? (pct > 100 ? "+" : "") : ""}{(pct - 100).toFixed(1)}%
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {/* Progress bar — visible while generating */}
          {detailProgress && (
            <div className="pt-2 border-t border-border space-y-1">
              <div className="h-2 w-full bg-muted rounded overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.round((detailProgress.done / detailProgress.total) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground truncate">{detailProgress.label}</p>
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDetailPickerOpen(false)}
              disabled={detailBusy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateDetailedReport}
              disabled={detailBusy || detailSelectedIdxs.length === 0}
              data-testid="detail-report-generate"
            >
              {detailBusy ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating…</>
              ) : (
                <><FileDown className="h-4 w-4 mr-1" /> Generate ({detailSelectedIdxs.length} room{detailSelectedIdxs.length !== 1 ? "s" : ""})</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
